import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DivisionMapping, Entry, Event, Level } from '@wht-xc-utils/shared/domain';
import { CANONICAL_LEVELS, type LevelSlug } from '@wht-xc-utils/shared/domain';

import { EventTabs } from '../components/EventTabs';
import { api } from '../lib/api';

export function EntriesPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const qc = useQueryClient();

  const event = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.get<{ event: Event }>(`/api/events/${eventId}`),
    enabled: !!eventId,
  });
  const entries = useQuery({
    queryKey: ['entries', eventId],
    queryFn: () => api.get<{ entries: Entry[] }>(`/api/events/${eventId}/entries`),
    enabled: !!eventId,
  });
  const levels = useQuery({
    queryKey: ['levels', eventId],
    queryFn: () => api.get<{ levels: Level[] }>(`/api/events/${eventId}/levels`),
    enabled: !!eventId,
  });
  const mappings = useQuery({
    queryKey: ['divisionMappings', eventId],
    queryFn: () =>
      api.get<{ divisionMappings: DivisionMapping[] }>(
        `/api/events/${eventId}/division-mappings`,
      ),
    enabled: !!eventId,
  });

  const storedShowId = event.data?.event.showConnectId ?? '';
  const [showId, setShowId] = useState('');
  // Sync the input with the persisted value whenever the event row
  // is (re-)fetched. On first load this prefills the field; after a
  // successful import the cache invalidation re-runs this and the
  // input snaps to the newly-saved id.
  useEffect(() => {
    setShowId(storedShowId);
  }, [storedShowId]);

  const importShowConnect = useMutation({
    mutationFn: () =>
      api.post<{ summary: { entriesUpserted: number; divisionsUnmapped: string[] } }>(
        `/api/events/${eventId}/entries/import-showconnect`,
        { showId },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId] });
      qc.invalidateQueries({ queryKey: ['entries', eventId] });
      qc.invalidateQueries({ queryKey: ['levels', eventId] });
      qc.invalidateQueries({ queryKey: ['divisionMappings', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
    },
  });

  const isRefresh = storedShowId !== '' && showId.trim() === storedShowId;
  const buttonLabel = importShowConnect.isPending
    ? isRefresh
      ? 'Refreshing…'
      : 'Importing…'
    : isRefresh
      ? 'Refresh entries'
      : 'Import entries';

  const updateMapping = useMutation({
    mutationFn: (
      input:
        | { divisionName: string; levelId: string | null }
        | { divisionName: string; canonicalSlug: LevelSlug },
    ) =>
      api.put<{ divisionMapping: DivisionMapping }>(
        `/api/events/${eventId}/division-mappings`,
        { ...input, source: 'manual' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['divisionMappings', eventId] });
      qc.invalidateQueries({ queryKey: ['entries', eventId] });
      qc.invalidateQueries({ queryKey: ['levels', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
    },
  });

  const deleteEntry = useMutation({
    mutationFn: (id: string) => api.delete(`/api/events/${eventId}/entries/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entries', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
    },
  });

  const entriesByDivision = new Map<string, Entry[]>();
  for (const e of entries.data?.entries ?? []) {
    const key = e.divisionName || '(no division)';
    const list = entriesByDivision.get(key) ?? [];
    list.push(e);
    entriesByDivision.set(key, list);
  }
  const sortedDivisions = [...entriesByDivision.keys()].sort();

  const acceptedCounts = new Map<string, { Accepted: number; total: number }>();
  for (const e of entries.data?.entries ?? []) {
    const k = e.divisionName || '(no division)';
    const v = acceptedCounts.get(k) ?? { Accepted: 0, total: 0 };
    v.total += 1;
    if (e.status === 'Accepted') v.Accepted += 1;
    acceptedCounts.set(k, v);
  }

  const mappingByDivision = new Map(
    (mappings.data?.divisionMappings ?? []).map((m) => [m.divisionName, m]),
  );
  const eventLevels = levels.data?.levels ?? [];
  const existingSlugs = new Set(eventLevels.map((level) => level.canonicalSlug));
  const missingCanonicalLevels = CANONICAL_LEVELS.filter(
    (level) => !existingSlugs.has(level.slug),
  );

  return (
    <>
      <EventTabs />

      <div className="card">
        <h2>Import from ShowConnect</h2>
        {storedShowId ? (
          <p className="muted">
            Saved show id for this event: <code>{storedShowId}</code>.
          </p>
        ) : null}
        <div className="toolbar">
          <input
            type="text"
            placeholder="ShowConnect show id (e.g. 12345)"
            value={showId}
            onChange={(e) => setShowId(e.target.value)}
            style={{ maxWidth: 280 }}
          />
          <button
            className="primary"
            disabled={!showId.trim() || importShowConnect.isPending}
            onClick={() => importShowConnect.mutate()}
          >
            {buttonLabel}
          </button>
          {importShowConnect.data ? (
            <span className="success">
              Upserted {importShowConnect.data.summary.entriesUpserted} entries
              {importShowConnect.data.summary.divisionsUnmapped.length > 0
                ? ` • Unmapped: ${importShowConnect.data.summary.divisionsUnmapped.join(', ')}`
                : null}
            </span>
          ) : null}
          {importShowConnect.error ? (
            <span className="error">{(importShowConnect.error as Error).message}</span>
          ) : null}
        </div>
      </div>

      <div className="card">
        <h2>Division → Level mapping</h2>
        {sortedDivisions.length === 0 ? (
          <p className="muted">No entries yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Division</th>
                <th>Accepted / Total</th>
                <th>Level</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {sortedDivisions.map((div) => {
                const m = mappingByDivision.get(div);
                const counts = acceptedCounts.get(div) ?? { Accepted: 0, total: 0 };
                return (
                  <tr key={div}>
                    <td>{div}</td>
                    <td>
                      {counts.Accepted} / {counts.total}
                    </td>
                    <td>
                      <select
                        value={m?.levelId ? `level:${m.levelId}` : ''}
                        onChange={(e) => {
                          const target = e.target.value;
                          if (target.startsWith('level:')) {
                            updateMapping.mutate({
                              divisionName: div,
                              levelId: target.slice('level:'.length),
                            });
                          } else if (target.startsWith('canonical:')) {
                            updateMapping.mutate({
                              divisionName: div,
                              canonicalSlug: target.slice('canonical:'.length) as LevelSlug,
                            });
                          } else {
                            updateMapping.mutate({ divisionName: div, levelId: null });
                          }
                        }}
                      >
                        <option value="">(unmapped)</option>
                        {eventLevels.length > 0 ? (
                          <optgroup label="Event levels">
                            {eventLevels.map((l) => (
                              <option key={l.id} value={`level:${l.id}`}>
                                {l.displayName}
                              </option>
                            ))}
                          </optgroup>
                        ) : null}
                        {missingCanonicalLevels.length > 0 ? (
                          <optgroup label="Add level to event">
                            {missingCanonicalLevels.map((l) => (
                              <option key={l.slug} value={`canonical:${l.slug}`}>
                                {l.displayName}
                              </option>
                            ))}
                          </optgroup>
                        ) : null}
                      </select>
                    </td>
                    <td>
                      <span
                        className={`pill ${m?.source === 'unresolved' ? 'warn' : 'success'}`}
                      >
                        {m?.source ?? '—'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h2>Entries</h2>
        {entries.isLoading ? (
          <p className="muted">Loading…</p>
        ) : (
          sortedDivisions.map((div) => (
            <div key={div}>
              <h3>{div}</h3>
              <table className="aligned-table">
                <colgroup>
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '26%' }} />
                  <col style={{ width: '26%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '6%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Pinny</th>
                    <th>Rider</th>
                    <th>Horse</th>
                    <th>Status</th>
                    <th>XC ride time</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(entriesByDivision.get(div) ?? []).map((e) => (
                    <tr key={e.id}>
                      <td>{e.pinny ?? '—'}</td>
                      <td>{e.riderName}</td>
                      <td>{e.horseName}</td>
                      <td>
                        <span
                          className={`pill ${
                            e.status === 'Accepted' ? 'success' : 'warn'
                          }`}
                        >
                          {e.status}
                        </span>
                      </td>
                      <td>{e.xcRideTimeRaw ?? '—'}</td>
                      <td className="right">
                        <button
                          className="subtle danger"
                          onClick={() => {
                            if (confirm(`Delete ${e.riderName}?`)) deleteEntry.mutate(e.id);
                          }}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>

      <div className="card">
        <h2>Next: review the schedule</h2>
        <p className="muted">
          Competition days and level order can be derived from accepted
          entries' cross-country ride times.
        </p>
        <div className="workflow-actions">
          {(entries.data?.entries.length ?? 0) > 0 ? (
            <Link
              className="button-link primary"
              to={`/events/${eventId}/schedule`}
            >
              Continue to schedule
            </Link>
          ) : (
            <button className="primary" disabled>
              Import entries to continue
            </button>
          )}
        </div>
      </div>
    </>
  );
}
